import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraDataTable, SpecraEmptyState, SpecraHeading, SpecraIconButton, SpecraInlineBanner, SpecraLink, SpecraListPage, SpecraPageHeader, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { CalendarDays, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ErrorState, LoadingState } from '../../../components/layout';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { usePortfolioDashboard } from '../CommandCenter/hooks';
import { CmStatusBadge } from '../Deliverables/components/common';
import { cmDate, cmMonth, todayISO } from '../Deliverables/lib/format';
import { occurrenceStatus } from '../Deliverables/lib/status';
import { monthSpan, shiftMonth, useDeliverableDueDates, useDeliveryCalendar } from './hooks';
const EVENT_TYPE_LABEL = {
    pop_option_expiration: 'Option-period expiration',
    protest_window: 'Protest window',
    debrief_window: 'Debrief window'
};
function eventTypeLabel(t) {
    return EVENT_TYPE_LABEL[t] ?? (t.replace(/_/g, ' ') || 'Event');
}
// Materialized deadlines are UTC-midnight instants: their day is the UTC one.
function eventDay(ev) {
    return ev.dueAt ? ev.dueAt.toDate().toISOString().slice(0, 10) : '';
}
export function CalendarPage() {
    const companyId = useTenantId();
    const [params, setParams] = useSearchParams();
    const month = /^\d{4}-\d{2}$/.test(params.get('month') ?? '') ? params.get('month') : todayISO().slice(0, 7);
    const setMonth = (m)=>setParams((prev)=>{
            const next = new URLSearchParams(prev);
            next.set('month', m);
            return next;
        });
    const { from, to } = monthSpan(month);
    const calendar = useDeliveryCalendar({
        companyId
    });
    const dashboard = usePortfolioDashboard({
        companyId
    });
    const programs = dashboard.data?.programs;
    const programIds = useMemo(()=>(programs ?? []).map((p)=>p.programId), [
        programs
    ]);
    const programName = useMemo(()=>new Map((programs ?? []).map((p)=>[
                p.programId,
                p.programName || p.contractNumber
            ])), [
        programs
    ]);
    const deliverables = useDeliverableDueDates(programIds, month);
    const hiddenCount = calendar.data?.hiddenCount ?? 0;
    const rows = [
        ...deliverables.dates.map((due)=>({
                kind: 'deliverable',
                day: due.occurrence.dueOn,
                key: `occ:${due.occurrence.id}`,
                due
            })),
        ...(calendar.data?.events ?? []).map((event)=>({
                kind: 'event',
                day: eventDay(event),
                key: `ev:${event.id}`,
                event
            })).filter((r)=>r.day >= from && r.day <= to)
    ].sort((a, b)=>a.day.localeCompare(b.day));
    const isLoading = calendar.isLoading || dashboard.isLoading || deliverables.isLoading;
    const isFetching = calendar.isFetching || deliverables.isFetching;
    const refresh = ()=>{
        void calendar.refetch();
        deliverables.refetch();
    };
    // One notes band: access, partial-load and failure notes together.
    const partial = dashboard.isError && !calendar.isError || deliverables.failed > 0;
    const hasNotes = hiddenCount > 0 || calendar.isError || partial;
    const stepper = /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center gap-2",
        children: [
            /*#__PURE__*/ _jsx(SpecraIconButton, {
                "data-testid": Testids.cmPortfolio.calendarPrev,
                label: "Previous month",
                onClick: ()=>setMonth(shiftMonth(month, -1)),
                size: "sm",
                variant: "ghost",
                children: /*#__PURE__*/ _jsx(ChevronLeft, {})
            }),
            /*#__PURE__*/ _jsx(SpecraHeading, {
                as: "h2",
                className: "min-w-40 text-center",
                "data-testid": Testids.cmPortfolio.calendarMonth,
                size: "ui",
                children: cmMonth(from)
            }),
            /*#__PURE__*/ _jsx(SpecraIconButton, {
                "data-testid": Testids.cmPortfolio.calendarNext,
                label: "Next month",
                onClick: ()=>setMonth(shiftMonth(month, 1)),
                size: "sm",
                variant: "ghost",
                children: /*#__PURE__*/ _jsx(ChevronRight, {})
            })
        ]
    });
    return /*#__PURE__*/ _jsx(SpecraListPage, {
        "data-testid": Testids.calendar.page,
        header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
            actions: /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.calendar.refreshButton,
                disabled: isFetching,
                iconLeft: /*#__PURE__*/ _jsx(RefreshCw, {
                    className: isFetching ? 'animate-spin' : undefined
                }),
                onClick: refresh,
                size: "sm",
                variant: "secondary",
                children: "Refresh"
            }),
            description: "Cross-project deadlines — deliverable due dates, option-period expirations, and protest/debrief windows across all authorized programs.",
            title: "Delivery Calendar"
        }),
        toolbarProps: {
            search: false,
            viewControl: stepper
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            children: [
                hasNotes && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                    tone: calendar.isError || partial ? 'danger' : 'neutral',
                    variant: "framed",
                    children: [
                        hiddenCount > 0 && /*#__PURE__*/ _jsxs("div", {
                            "data-testid": Testids.calendar.hiddenCount,
                            children: [
                                hiddenCount,
                                ' ',
                                hiddenCount === 1 ? 'program’s deadlines are' : 'programs’ deadlines are',
                                " hidden because you don’t have access to ",
                                hiddenCount === 1 ? 'it' : 'them',
                                "."
                            ]
                        }),
                        calendar.isError && /*#__PURE__*/ _jsx("div", {
                            children: "Option-period and protest/debrief deadlines couldn’t be loaded; only deliverable due dates are listed."
                        }),
                        partial && /*#__PURE__*/ _jsx("div", {
                            "data-testid": Testids.cmPortfolio.partialNote,
                            children: dashboard.isError && !calendar.isError ? 'Your programs couldn’t be loaded, so deliverable due dates are missing from this list.' : `Deliverable due dates couldn’t be loaded for ${deliverables.failed} ${deliverables.failed === 1 ? 'program' : 'programs'}.`
                        })
                    ]
                }),
                isLoading ? /*#__PURE__*/ _jsx(LoadingState, {
                    rows: 3
                }) : dashboard.isError && calendar.isError ? /*#__PURE__*/ _jsx(ErrorState, {
                    message: "Couldn’t load the delivery calendar. This is a load error, not an empty calendar.",
                    onRetry: ()=>{
                        void dashboard.refetch();
                        refresh();
                    }
                }) : rows.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>setMonth(shiftMonth(month, 1)),
                        size: "sm",
                        variant: "secondary",
                        children: "Next month"
                    }),
                    "data-testid": Testids.pageState.empty,
                    description: "Deliverable due dates, option-period expirations and protest/debrief windows for your programs appear here. Try another month.",
                    framed: true,
                    icon: /*#__PURE__*/ _jsx(CalendarDays, {}),
                    title: `No deadlines in ${cmMonth(from)}`
                }) : /*#__PURE__*/ _jsx(SpecraDataTable, {
                    columns: columns(programName),
                    getRowId: (r)=>r.key,
                    getRowProps: rowProps,
                    rows: rows,
                    rowTestId: Testids.calendar.eventRow,
                    testId: Testids.calendar.eventList
                })
            ]
        })
    });
}
function rowProps(r) {
    return r.kind === 'deliverable' ? {
        'data-source-id': r.due.occurrence.id,
        'data-program-id': r.due.programId,
        'data-kind': 'deliverable'
    } : {
        'data-source-id': r.event.sourceObjectId,
        'data-program-id': r.event.programId,
        'data-kind': r.event.eventType
    };
}
function columns(programName) {
    return [
        {
            id: 'title',
            header: 'Deadline',
            cell: (r)=>r.kind === 'deliverable' ? // A deliverable date opens its drawer on the program's Deliverables page.
                /*#__PURE__*/ _jsx(SpecraLink, {
                    className: "font-medium",
                    href: `/delivery/programs/${r.due.programId}/deliverables?occ=${r.due.occurrence.id}`,
                    renderLink: renderRouterLink,
                    children: [
                        r.due.title || 'Deliverable',
                        r.due.occurrence.label
                    ].filter(Boolean).join(' · ')
                }) : /*#__PURE__*/ _jsx(SpecraText, {
                    className: "font-medium",
                    children: r.event.title || eventTypeLabel(r.event.eventType)
                })
        },
        {
            id: 'program',
            header: 'Program',
            cell: (r)=>(r.kind === 'deliverable' ? programName.get(r.due.programId) : r.event.programName) || 'Unnamed program'
        },
        {
            id: 'type',
            header: 'Type',
            cell: (r)=>/*#__PURE__*/ _jsx(SpecraText, {
                    tone: "muted",
                    children: r.kind === 'deliverable' ? 'Deliverable due' : eventTypeLabel(r.event.eventType)
                })
        },
        {
            id: 'status',
            header: 'Status',
            cell: (r)=>r.kind === 'deliverable' ? /*#__PURE__*/ _jsx(CmStatusBadge, {
                    status: occurrenceStatus(r.due.occurrence, r.due.finalReviewTier)
                }) : null
        },
        {
            id: 'date',
            header: 'Date',
            align: 'right',
            cell: (r)=>r.kind === 'deliverable' ? cmDate(r.due.occurrence.dueOn) : cmDate(eventDay(r.event), 'No date')
        }
    ];
}
